import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const SessionWarning = () => {
  const { sessionWarning, warningLevel, extendSession, signOut } = useAuth();
  const [countdown, setCountdown] = useState(600); // Default 10 minutes
  const [isVisible, setIsVisible] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  useEffect(() => {
    // Check current notification permission status (don't request automatically)
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Get warning configuration based on level
  const getWarningConfig = useCallback(() => {
    switch(warningLevel) {
      case 'initial':
        return {
          title: 'Session Expiring Soon',
          message: 'Your session will expire in',
          bgColor: 'bg-yellow-500',
          borderColor: 'border-yellow-400',
          textColor: 'text-yellow-900',
          countdown: 600, // 10 minutes
          soundVolume: 0.3,
          icon: '⏰'
        };
      case 'critical':
        return {
          title: '⚠️ URGENT: Session Expiring',
          message: 'You will be logged out in',
          bgColor: 'bg-orange-600',
          borderColor: 'border-orange-500',
          textColor: 'text-orange-100',
          countdown: 300, // 5 minutes
          soundVolume: 0.5,
          icon: '⚠️',
          pulse: true
        };
      case 'final':
        return {
          title: '🚨 FINAL WARNING',
          message: 'LOGGING OUT IN',
          bgColor: 'bg-red-600',
          borderColor: 'border-red-500',
          textColor: 'text-white',
          countdown: 60, // 1 minute
          soundVolume: 0.8,
          icon: '🚨',
          pulse: true,
          urgent: true
        };
      default:
        return {
          title: 'Session Expiring Soon',
          message: 'Your session will expire in',
          bgColor: 'bg-yellow-500',
          borderColor: 'border-yellow-400',
          textColor: 'text-yellow-900',
          countdown: 600,
          soundVolume: 0.3,
          icon: '⏰'
        };
    }
  }, [warningLevel]);

  useEffect(() => {
    if (sessionWarning) {
      setIsVisible(true);
      const config = getWarningConfig();
      setCountdown(config.countdown);

      // Play notification sound with level-appropriate volume
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTUGHGS48OGmWRQLPqTc8cVuIAUwe9L1342DAjROq+73rnkZBSVltuT1pJY9FGev5O60fxcEM2W24OquVxELULnh86xqIAg+kM/yvnEeByB3x/XefEcJFV6y5e6xWxgHMpPO9c1wIwU4h9T01HYkAi9/z/PYdScELHXG8dh6NwUoedDy1HwnBCxzw/DYejgELHfH8dh7OAUkb8Xx2Xs4BDZZ0Dszjja2Lll7mERwFX4wjCkqNIoAAHicAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA');
        audio.volume = config.soundVolume;

        // Play multiple times for urgent warnings
        if (config.urgent) {
          audio.play().catch(() => {});
          setTimeout(() => audio.play().catch(() => {}), 500);
          setTimeout(() => audio.play().catch(() => {}), 1000);
        } else {
          audio.play().catch(() => {});
        }
      } catch (e) {
        // Ignore audio errors
      }

      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            // Auto-logout when countdown reaches 0
            signOut();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    } else {
      setIsVisible(false);
    }
  }, [sessionWarning, signOut, getWarningConfig]);

  const handleExtendSession = async () => {
    try {
      console.log('[SessionWarning] Extending session...');
      await extendSession();
      setIsVisible(false);

      // Close any open notifications (service worker API not available in regular window context)
      // This would require a service worker implementation
    } catch (error) {
      console.error('[SessionWarning] Failed to extend session:', error);
      // Show error message to user
      alert('Failed to extend session. Please try again or refresh the page.');
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isVisible) return null;

  const config = getWarningConfig();

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] ${
        config.pulse ? 'animate-pulse' : ''
      }`}
      style={{
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div className={`${config.bgColor} p-6 rounded-lg shadow-2xl max-w-md w-full mx-auto border-2 ${config.borderColor} ${
        warningLevel === 'final' ? 'animate-shake' : ''
      }`}
        style={{
          maxWidth: '28rem',
          margin: '0 1rem'
        }}
      >
        <div className="flex items-center mb-4">
          <div className="text-3xl mr-3 flex-shrink-0">
            {config.icon}
          </div>
          <h3 className={`text-xl font-bold ${config.textColor}`}>
            {config.title}
          </h3>
        </div>

        <p className={`${config.textColor} mb-4 text-lg`}>
          {config.message} <span className={`font-bold text-2xl ${
            warningLevel === 'final' ? 'animate-flash' : ''
          }`}>{formatTime(countdown)}</span>
          {warningLevel !== 'final' && (
            <span className="block text-sm mt-2 opacity-90">
              You will be automatically logged out unless you extend your session.
            </span>
          )}
        </p>

        {notificationPermission === 'default' && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            <button
              onClick={() => {
                Notification.requestPermission().then(permission => {
                  setNotificationPermission(permission);
                });
              }}
              className="text-blue-500 underline hover:text-blue-600"
            >
              Enable notifications
            </button> to receive alerts when your session is about to expire.
          </p>
        )}
        
        <div className="flex space-x-3">
          <button
            onClick={handleExtendSession}
            className={`flex-1 ${
              warningLevel === 'final'
                ? 'bg-white text-red-600 font-bold text-lg py-3 animate-pulse'
                : 'bg-white/90 text-gray-800 py-2'
            } px-4 rounded-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-white transition-all shadow-lg`}
          >
            {warningLevel === 'final' ? 'STAY LOGGED IN!' : 'Stay Logged In'}
          </button>
          <button
            onClick={signOut}
            className={`flex-1 bg-black/20 ${config.textColor} px-4 py-2 rounded-md hover:bg-black/30 focus:outline-none focus:ring-2 focus:ring-black/50 transition-colors`}
          >
            Logout Now
          </button>
        </div>
      </div>
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        @keyframes flash {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.5; }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out infinite;
        }
        .animate-flash {
          animation: flash 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SessionWarning;